import React from 'react';
import {
  BookingRequestResponse,
  Organizer,
  OrganizerPortalEvent,
  OrganizerVerification,
  PREMIUM_BENEFITS,
  PROMOTION_TIER_SALES,
  activePromotion,
  formatEuro,
  vatNote,
} from '@radevents/shared';
import { BERRY, INK } from '../organizerSpace';
import PremiumStatus from '../PremiumStatus';
import { CheckBadge, LockBadge, PremiumCta } from './chrome';
import { CalendarDays } from 'lucide-react';
import PremiumBenefitIcon from '../PremiumBenefitIcon';
import { favoriteTeaser } from './favoriteTeaser';
import { useBookingOffer } from './useBookingOffer';
import { openPromotionRequest, promoActionLabel } from './promoActions';

interface Props {
  organizerId: number;
  /** Läuft Premium? Entscheidet über Verkaufs- oder Zustandsansicht. */
  premium: boolean;
  /** Die volle Premium-Buchung des eigenen Vereins — auch geplant oder abgelaufen. */
  verification?: OrganizerVerification;
  /** Es liegt schon eine Premium-Anfrage bei uns. */
  requested?: boolean;
  /** Stammdaten — für den Zustand von Logo und Banner. */
  profile: Organizer | null;
  /**
   * Liegt ein hochgeladenes Bild noch in der Prüfung?
   *
   * `profile` kennt nur das **freigegebene** Bild (`logoUploaded`/`logo_url`) — das ist die
   * öffentliche Auslieferung. Ein gerade hochgeladenes wartet daneben (V141) und ist nur über
   * den eigenen Abruf sichtbar. Ohne diese Angabe meldet die Zeile „Fehlt noch", während oben
   * im Kopf „Wird geprüft" am Bild steht: zwei Aussagen über dieselbe Datei.
   */
  pendingAssets?: { logo?: boolean; banner?: boolean };
  /** Die eigenen Events, für die Übersicht der Hervorhebungen. */
  events: OrganizerPortalEvent[];
  /** Offene Buchungsanfragen — sie entscheiden, ob am Event „Anfrage läuft" steht. */
  bookings: BookingRequestResponse[];
  /** Öffnet den Buchungsdialog für genau dieses Event. */
  onPromote: (entry: OrganizerPortalEvent) => void;
  /** Öffnet den Premium-Dialog mit voller Erklärung — für die Haken an gesperrten Stellen. */
  onPremiumHint: () => void;
  /** Öffnet denselben Dialog als reine Bestellung: die Karte hat die Leistungen schon genannt. */
  onOrder: () => void;
  /**
   * Wechselt zum Reiter *Meine Events* und springt dort an genau dieses Event.
   *
   * Ohne die Id landete der Verein oben in einer Liste gleich aussehender Karten und musste den
   * Termin, den er gerade angetippt hatte, ein zweites Mal suchen.
   */
  onGoToEvents: (eventId: number) => void;
  /** Einstieg aus „Event hervorheben": stellt die Eventauswahl vor alle anderen Inhalte. */
  promotionIntent?: boolean;
}

/**
 * Reiter *Sichtbarkeit & Promotion*: was der Auftritt gerade hergibt und was ihn stärker macht.
 *
 * Vorher stand hier eine einzige Karte („Noch nicht freigeschaltet") plus ein Absatz über den
 * Zahlungsablauf — ein Reiter, der nur aus einem Schloss besteht. Jetzt beantwortet er vier
 * Fragen in der Reihenfolge, in der ein Vorstand sie stellt: *Was habe ich?* (Premium-Zustand),
 * *Wie sieht meine Seite aus?* (Profil), *Wen erreiche ich?* (Interessierte), *Was kann ich für
 * einzelne Events tun?* (Hervorhebungen).
 *
 * **Hier wird auch gebucht** (02.09.2026). Bis dahin galt „er verkauft, aber er bucht nicht":
 * hervorgehoben wurde nur am Termin im Reiter *Meine Events*, hier stand allein ein „Zum Event".
 * Das Argument — ein zweiter Buchungsknopf wären zwei Türen in denselben Raum — trägt nicht: es
 * ist **ein** Dialog, zweimal geöffnet, und die Entscheidung, was an einem Event überhaupt noch
 * zu kaufen ist, liegt als `promoActionLabel` an einer Stelle, die sich beide Reiter teilen.
 * Was blieb, war ein Abschnitt mit der Überschrift *Events hervorheben*, in dem sich nichts
 * hervorheben ließ.
 */
const VisibilityTab: React.FC<Props> = ({
  organizerId, premium, verification, requested, profile, events, bookings, pendingAssets,
  onPremiumHint, onOrder, onPromote, onGoToEvents, promotionIntent,
}) => {
  const { premiumPrice, promotionFrom, vatPercent } = useBookingOffer(organizerId);

  /** Premium läuft seit höchstens sieben Tagen — dann ist die Freischaltung noch eine Nachricht. */
  const justUnlocked = !!verification?.from
    && (Date.now() - new Date(verification.from).getTime()) < 7 * 86_400_000;

  const hasLogo = !!(profile?.logoUploaded || profile?.logoUrl);
  const hasBanner = !!(profile?.bannerUploaded || profile?.photoUrl);

  /** Nur kommende Events: was gelaufen ist, lässt sich nicht mehr bewerben. */
  const upcoming = events.filter(e => e.event.eventDate >= new Date().toISOString().slice(0, 10));
  const favorites = events.reduce((sum, e) => sum + (e.event.favoriteCount ?? 0), 0);
  /** Ohne Premium darf genau eine Zahl heraus: das staerkste Event als Vorgeschmack. */
  const teaser = favoriteTeaser(events);

  /**
   * Der Anlass, der gerade wirklich vorliegt.
   *
   * Die Erinnerung geht 7 Tage vor der Veranstaltung raus (`FAVORITE_REMINDER`). Steht die
   * nächste in überschaubarer Zeit an, ist das keine erfundene Dringlichkeit, sondern ein Datum:
   * ab dann ist die Gelegenheit für dieses Event vorbei. Weiter als 45 Tage voraus sagt der Satz
   * nichts mehr — dann ist er nur Text.
   */
  const nextInDays = (() => {
    const next = [...upcoming].sort((a, b) => a.event.eventDate.localeCompare(b.event.eventDate))[0];
    if (!next) return null;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const days = Math.round(
      (new Date(`${next.event.eventDate}T00:00:00`).getTime() - today.getTime()) / 86_400_000,
    );
    // 21 Tage: darueber ist es kein Anlass mehr, sondern nur ein Datum.
    return days >= 0 && days <= 21 ? { days, title: next.event.title } : null;
  })();

  /**
   * Eine Zeile je Leistung: Zustand links, Weg rechts.
   *
   * `locked` heißt „gehört zu Premium" — dann führt die Zeile in den Dialog statt ins Leere.
   * Ohne diese Trennung stünde neben einer gesperrten Sache ein Knopf, der nichts tut.
   */
  const feature = (
    title: string, text: string,
    state: { label: string; done?: boolean; locked?: boolean; pending?: boolean },
    action?: { label: string; onClick: () => void; premium?: boolean },
  ) => (
    <div className="vb-feature" key={title}>
      <div className="vb-feature-main">
        <div className="vb-feature-head">
          {state.locked && <LockBadge />}
          <strong>{title}</strong>
          {/* Erledigt ist ein Haken, kein Wort: „STEHT" in einer grünen Pille sah aus wie eine
              Beschriftung, die man lesen muss, obwohl die Auskunft ja/nein ist. „Fehlt" bleibt
              dagegen Text — dort *ist* das Wort die Auskunft. */}
          {state.done ? <CheckBadge label={state.label} /> : (
            <span className={`vb-feature-state${state.pending ? ' vb-feature-state--pending' : ''}`}>
              {state.label}
            </span>
          )}
        </div>
        <p>{text}</p>
      </div>
      {action && (action.premium
        // Führt der Knopf zum Kauf, kennt er den Wartezustand: sonst lädt eine gesperrte Zeile
        // zum zweiten Bestellen ein, während die Anfrage schon bei uns liegt.
        ? <PremiumCta requested={requested} onClick={action.onClick} className="vb-btn">
            {action.label}
          </PremiumCta>
        : <button className="vb-btn" onClick={action.onClick}>{action.label}</button>
      )}
    </div>
  );

  /**
   * Öffnet den Dateidialog oben im Kopf — derselbe Weg wie der leere Platz dort.
   *
   * Eine Zeile, die einen fehlenden Zustand meldet, muss auch hinführen. Beim Titelbild fehlte
   * das zuerst: „Noch keins" stand ohne Knopf da, obwohl Premium lief und der Verein es hätte
   * hochladen dürfen — eine Sackgasse neben einer Zeile, die es besser machte.
   */
  const pickAsset = (kind: 'logo' | 'banner') => () => {
    document.querySelector<HTMLInputElement>(`input[type="file"][data-asset="${kind}"]`)?.click();
  };

  return (
    <section style={{ paddingBottom: 22, display: 'flex', flexDirection: 'column' }}>
      {/* --- 1. Was gilt gerade ------------------------------------------------------------ */}
      {/* Frisch freigeschaltet: eine Woche lang steht hier, was jetzt möglich ist.
          Zwischen Anfrage und Freischaltung liegen bei uns Tage — es gibt keinen
          Zahlungsanbieter, der Verein überweist, und irgendwann schalten wir frei. Ohne diese
          Karte merkt er davon nichts: er kommt irgendwann wieder, und die Sperren sind eben weg.
          Die Bedingung ist Daten, kein gemerkter Zustand: `from` liegt keine sieben Tage zurück. */}
      {premium && justUnlocked && (
        <div className="vb-welcome">
          <div className="vb-welcome-main">
            <div className="vb-sell-eyebrow" style={{ color: BERRY }}>Premium ist aktiv</div>
            <h3 className="vb-welcome-title">Deine automatischen Erinnerungen sind jetzt aktiv.</h3>
            <p>
              Wir erinnern sie 7 Tage vor jedem Event und sobald die Anmeldung offen ist. Dein
              Titelbild und die Zahlen zu deinen Events sind freigeschaltet.
            </p>
          </div>
        </div>
      )}

      {premium ? (
        <PremiumStatus verification={verification} variant="card" requested={requested}
          onExtend={onPremiumHint} />
      ) : (
        <div className="vb-sell">
          <div className="vb-sell-main">
            <span className="vb-sell-badge">Premium</span>
            <h3 className="vb-sell-title">
              Interessierte automatisch informieren.
            </h3>

            {/* Beleg vor Versprechen — und zwar seine eigene Zahl.

                **Keine Leistung in diesem Satz** (02.09.2026): was Premium kann, steht als
                Reihe darunter. Vorher endete der Satz auf „auch nicht, wenn die Anmeldung
                aufmacht", und ohne Vormerkungen stand hier die Erinnerungslogik komplett
                ausgeschrieben — also derselbe Punkt zweimal auf 20 cm Höhe, oben als Fließtext
                und unten als Stichpunkt. Der Satz macht jetzt nur noch auf, die Reihe zählt auf.

                Ohne Zahl bleibt kein Beleg übrig, also auch kein Ersatzbeleg: eine große Null
                wäre ein Argument gegen uns. Dann steht dort der Grund, aus dem ein Verein
                überhaupt weiterliest — er hat die Arbeit schon gemacht. */}
            {teaser ? (
              <p className="vb-sell-proof">
                <b>{teaser.favorites}</b> Personen haben „{teaser.title}" vorgemerkt. Mit Premium
                informieren wir sie zum Anmeldestart und sieben Tage vor dem Event.
              </p>
            ) : (
              <p className="vb-sell-proof">
                Premium informiert Interessierte automatisch und zeigt dir, wie deine Events genutzt werden.
              </p>
            )}

            {nextInDays && (
              <div className="vb-sell-urgent">
                <span className="vb-sell-urgent-icon" aria-hidden>
                  <CalendarDays size={18} strokeWidth={1.7} />
                </span>
                <div>
                  <strong>„{nextInDays.title}" ist in {nextInDays.days} {nextInDays.days === 1 ? 'Tag' : 'Tagen'}.</strong>
                  <span>
                    {nextInDays.days > 7
                      ? `Die Erinnerung ginge in ${nextInDays.days - 7} ${nextInDays.days - 7 === 1 ? 'Tag' : 'Tagen'} raus.`
                      : 'Die Erinnerung wäre längst raus.'}
                  </span>
                </div>
              </div>
            )}
          </div>

          <div className="vb-sell-side">
            {!premium && premiumPrice && (
              <div className="vb-sell-priceblock">
                <p className="vb-sell-price">{premiumPrice}</p>
                {/* Die Steuerangabe hängt am Preis, hier wie im Checkout und auf der
                    Angebotsseite. Ohne sie stünde im Veranstalter-Bereich eine andere Zahl als
                    auf der Rechnung, ohne dass jemand sagt, warum. */}
                {vatPercent != null && <p className="vb-sell-vat">{vatNote(vatPercent)}</p>}
              </div>
            )}
            {/* „Anfrage senden": der Knopf sagt, was gleich passiert. „Premium freischalten"
                versprach einen Zustand, den erst unsere Freigabe herstellt.

                Liegt die Anfrage schon bei uns, trägt er den Zustand und ist tot: eine zweite
                gibt es nicht (der Server weist sie ab), und „Anfrage ansehen" führte in denselben
                Dialog, der dann nur die Bestätigung noch einmal zeigte. */}
            <PremiumCta requested={requested} onClick={onOrder} className="vb-btn vb-sell-cta">
              Premium bestellen
            </PremiumCta>
            {/* Kein Satz unter dem Knopf (02.09.2026). Drei sind nacheinander weggefallen:
                „Alle Vorteile inklusive" stand über einer Reihe, die genau diese Vorteile
                aufzählt; „Anfrage stellen, danach zahlen. Kein Abo." erklärte den Ablauf ein
                zweites Mal, den die Bestätigung mit Betrag und Bankverbindung ohnehin nennt;
                und „Deine Anfrage liegt bei uns." erklärte den ausgegrauten Knopf, auf dem
                seitdem „Angefragt" steht. Der Zustand am Knopf ist die Auskunft. */}
          </div>

          {/* Eine Reihe aus vier Spalten: Symbol, Name, ein Satz — dieselben Texte wie im
              Dialog (PREMIUM_BENEFITS), also eine Pflegestelle. Vorher drei linksbündige Blöcke
              mit Werbeüberschrift über einem vierzeiligen Absatz; nebeneinander war das eine
              Textwand, von der der erste gelesen wird. `lead` bleibt hier bewusst ohne Wirkung:
              ein umrandeter Kasten in einer Viererreihe zerlegt die Reihe. */}
          <div className="vb-sell-perks">
            {PREMIUM_BENEFITS.map(b => (
              <div className="vb-sell-perk" key={b.title}>
                <span className="vb-sell-perk-icon"><PremiumBenefitIcon name={b.icon} /></span>
                <strong>{b.title}</strong>
                <span className="vb-sell-perk-text">{b.text}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* --- 2. Der eigene Auftritt --------------------------------------------------------- */}
      <div className="vb-eyebrow" style={{ margin: '30px 0 4px' }}>Dein Auftritt</div>
      <p className="vb-section-lead">So steht dein Verein auf Radsport-Events.</p>
      <div className="vb-features">
        {feature(
          'Logo',
          'Steht auf deiner Seite, an jedem Event und in der Veranstalterliste.',
          pendingAssets?.logo ? { label: 'Wird geprüft', pending: true }
            : hasLogo ? { label: 'Steht', done: true } : { label: 'Fehlt' },
          // Während der Prüfung kein Knopf: ersetzen geht am Bild oben, und ein zweiter Upload
          // von hier aus legte nur eine weitere Datei in dieselbe Warteschlange.
          (pendingAssets?.logo || hasLogo) ? undefined
            : { label: 'Logo wählen', onClick: pickAsset('logo') },
        )}
        {feature(
          'Titelbild',
          'Der breite Streifen über deinem Namen. Das Erste, was ein Besucher von dir sieht.',
          !premium ? { label: 'Premium', locked: true }
            : pendingAssets?.banner ? { label: 'Wird geprüft', pending: true }
              : hasBanner ? { label: 'Steht', done: true } : { label: 'Fehlt' },
          !premium ? { label: 'Jetzt freischalten', onClick: onPremiumHint, premium: true }
            : (pendingAssets?.banner || hasBanner) ? undefined
              : { label: 'Titelbild wählen', onClick: pickAsset('banner') },
        )}
        {/* Kostenlos seit dem 11.09.2026: 4 von 372 kommenden Events hatten ein Foto, und die
            Sperre schadete vor allem dem kostenlosen Kernprodukt. Der Weg ist das Formular am
            Event — deshalb steht hier kein Knopf, sondern der Hinweis, wo. */}
        {feature(
          'Bild und Flyer am Event',
          'Ein Foto und die Ausschreibung als PDF, direkt an der Veranstaltung. Beides trägst du beim Bearbeiten des Events ein.',
          { label: 'Inklusive', done: true },
        )}
      </div>

      {/* --- 3. Wen das erreicht ------------------------------------------------------------ */}
      {/* **Nur mit Premium.** Ohne Buchung stand hier dieselbe Werbung ein drittes Mal: die
          Verkaufskarte oben belegt sie mit einer echten Zahl, die Vorteils-Reihe zählt sie als
          Leistung auf, und hier stand sie noch einmal als Absatz mit eigenem Kaufknopf. Schlimmer
          als die Wiederholung war der Bezug: der Satz sprach von „diesen Leuten", während die
          Zahl daneben gesperrt blieb — ein Hinweis auf etwas, das gar nicht dasteht.

          Mit Premium ist der Abschnitt Auskunft statt Verkauf: die Summe und was mit ihr
          geschieht. */}
      {premium && (
        <>
          <div className="vb-eyebrow" style={{ margin: '30px 0 4px' }}>Automatische Erinnerungen</div>
          <p className="vb-section-lead">
            Wer eines deiner Events vormerkt, kann zum richtigen Zeitpunkt automatisch informiert werden.
          </p>
          <div className="vb-reach">
            {/* Die Favoritenzahl ist **echt** und steht ohnehin in jeder öffentlichen
                Event-Antwort. Bei **null** verliert sie die Markenfarbe (eine große Null in Beere
                liest sich wie ein Erfolg), und der Satz spricht nicht mehr von „diesen Leuten" —
                es gibt keine. Ein Text, der neben seiner eigenen Zahl unmöglich klingt, entwertet
                die ganze Auskunft. */}
            <div className={`vb-reach-count${favorites === 0 ? ' vb-reach-count--none' : ''}`}>
              <b>{favorites}</b>
              <span>{favorites === 1 ? 'Vormerkung' : 'Vormerkungen'} auf deine Events</span>
            </div>
            <p className="vb-reach-text">
              {favorites === 0
                ? 'Sobald jemand dein Event vormerkt, informieren wir die Person automatisch zum Anmeldestart und 7 Tage vor dem Event.'
                : 'Wir informieren diese Personen automatisch zum Anmeldestart und 7 Tage vor dem Event.'}
            </p>
          </div>
        </>
      )}

      {/* --- 4. Einzelne Events hervorheben -------------------------------------------------- */}
      {/* **Gebucht wird hier, nicht nur nebenan** (02.09.2026). Die Zeile trug lange allein ein
          „Zum Event" und schickte damit in den Reiter *Meine Events*, wo derselbe Dialog aufgeht.
          Das Argument dafür war, zwei Buchungsknöpfe wären zwei Wahrheiten — das trägt nicht:
          es ist **ein** Dialog, und die Entscheidung, was an einem Event noch zu kaufen ist,
          liegt seitdem als `promoActionLabel` an einer Stelle. Was blieb, war ein Abschnitt mit
          der Überschrift „Events hervorheben", in dem sich nichts hervorheben ließ.

          „Zum Event" bleibt als stiller zweiter Knopf: wer die Bühne buchen will, braucht dort
          ein Foto — der Weg dahin muss von hier aus offen sein. */}
      <div style={{ order: promotionIntent ? -1 : undefined }}>
      <div className="vb-eyebrow" style={{ margin: promotionIntent ? '0 0 4px' : '30px 0 4px' }}>
        {promotionIntent ? 'Hervorhebung ausgewählt' : 'Events hervorheben'}
      </div>
      {/* Zwei Sätze: was es bringt, was es kostet. Der genannte Preis trägt den Premium-Rabatt
          bereits (der Server rechnet ihn in die Beträge), deshalb sagt der zweite Halbsatz mit
          Premium, warum er niedriger ist als im Schaufenster. Ohne Premium steht der Rabatt hier
          **nicht** als Köder: ein Rabatt ist kein Vorteil (`docs/monetarisierung.md`). */}
      <p className="vb-section-lead">
        {promotionIntent
          ? 'Wähle das Event aus, das du hervorheben möchtest. Preis und Vorschau siehst du vor der Bestellung. '
          : 'Einzelne Events fallen in der Eventliste stärker auf, bis zur Veranstaltung. '}
        {promotionFrom == null
          ? 'Unabhängig von Premium buchbar.'
          : premium
            ? <>{formatEuro(promotionFrom)} je Event{vatPercent != null ? ` ${vatNote(vatPercent)}` : ''},{' '}
              <strong style={{ color: BERRY }}>dein Premium-Rabatt von 20 % ist
              eingerechnet</strong>.</>
            : <>{formatEuro(promotionFrom)} je Event{vatPercent != null ? ` ${vatNote(vatPercent)}` : ''},
              unabhängig von Premium.</>}
      </p>
      {upcoming.length === 0 ? (
        <div className="vb-empty">
          <p style={{ fontSize: 15, color: INK, margin: 0 }}>Keine kommenden Events.</p>
        </div>
      ) : (
        <div className="vb-features">
          {upcoming.map(entry => {
            const promo = activePromotion(entry.event);
            const action = promoActionLabel(entry);
            const open = openPromotionRequest(bookings, entry.event.id);
            const date = new Date(`${entry.event.eventDate}T00:00:00`)
              .toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
            return (
              <div className="vb-feature" key={entry.event.id}>
                <div className="vb-feature-main">
                  <div className="vb-feature-head">
                    <strong>{entry.event.title}</strong>
                    {/* Beschriftet wird die **Ausnahme**, nicht der Normalfall. „NICHT
                        HERVORGEHOBEN" stand an jeder Zeile und unterschied damit nichts — eine
                        Pille, die nur Platz und Aufmerksamkeit kostet. Was läuft, trägt seine
                        Stufe; was nicht läuft, trägt nichts. */}
                    {promo && (
                      <span className="vb-feature-state vb-feature-state--on">
                        {PROMOTION_TIER_SALES[promo.tier].name} bis{' '}
                        {new Date(promo.until).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}
                      </span>
                    )}
                  </div>
                  <p>
                    {date}
                    {entry.promotionStats
                      ? ` · ${entry.promotionStats.views} Aufrufe, ${entry.promotionStats.clicks}× auf deinen Link geklickt`
                      : ''}
                  </p>
                </div>
                <div className="vb-feature-actions">
                  {action && (
                    open
                      // Wie im Reiter *Meine Events*: der Knopf bleibt stehen und sagt, warum er
                      // gerade nicht geht. Eine zweite Anfrage weist der Server ohnehin ab.
                      ? (
                        <button className="vb-btn" disabled title="Es liegt bereits eine Anfrage vor.">
                          Bestellung läuft
                        </button>
                      )
                      : (
                        <button className="vb-btn vb-btn--primary" onClick={() => onPromote(entry)}>
                          {action}
                        </button>
                      )
                  )}
                  <button className="vb-btn" onClick={() => onGoToEvents(entry.event.id)}>
                    Zum Event
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      </div>
    </section>
  );
};

export default VisibilityTab;
